import React, { useState, useEffect } from 'react';
import { logPageView } from '../utils/analytics';
import { useUser } from '../contexts/UserContext';
import { getOrganizationalUnits, getUsers, getRoleAssignments, testDomainSetup, getDelegationInfo } from '../firebase';
import { Building2, Users, Shield, TrendingUp, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import RoleVerification from '../components/RoleVerification';

const Dashboard = () => {
  const { user } = useUser();
  const [stats, setStats] = useState({ organisationalUnits: 0, users: 0, delegatedRoles: 0 });
  const [loading, setLoading] = useState(true);
  const [domainTest, setDomainTest] = useState(null);
  const [testingDomain, setTestingDomain] = useState(false);
  const [delegationInfo, setDelegationInfo] = useState(null);

  useEffect(() => {
    // Log page view
    logPageView('Dashboard');

    const fetchStats = async () => {
      try {
        setLoading(true);
        const [ouResponse, usersResponse, rolesResponse] = await Promise.allSettled([
          getOrganizationalUnits(),
          getUsers(),
          getRoleAssignments()
        ]);
        setStats({
          organisationalUnits: ouResponse.status === 'fulfilled' ? (ouResponse.value.data.data?.length || 0) : 0,
          users: usersResponse.status === 'fulfilled' ? (usersResponse.value.data.data?.length || 0) : 0,
          delegatedRoles: rolesResponse.status === 'fulfilled' ? (rolesResponse.value.data.data?.length || 0) : 0
        });
      } finally { setLoading(false); }
    };

    const fetchDelegationInfo = async () => {
      try {
        const res = await getDelegationInfo();
        if (res.data?.success) setDelegationInfo(res.data);
      } catch (e) { /* ignore */ }
    };

    if (user) { fetchStats(); fetchDelegationInfo(); }
  }, [user]);

  const testDomainWideDelegation = async () => {
    try {
      setTestingDomain(true);
      const response = await testDomainSetup();
      if (response.data.success) {
        setDomainTest({ success: true, customerId: response.data.customerId, message: response.data.message });
      } else {
        setDomainTest({ success: false, message: response.data.message });
      }
    } catch (error) {
      setDomainTest({ success: false, message: 'Failed to test domain setup.' });
    } finally { setTestingDomain(false); }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">Welcome to your Google Workspace Admin Manager</p>
        </div>

        {/* Processing Notice - shown during loading */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">⚠️ Loading Dashboard Data</h3>
              <p className="text-sm text-blue-700 mt-1">
                <strong>Please wait...</strong> Loading organisational units, users, and role data from Google Workspace. 
                This may take a few moments to complete.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome to your Google Workspace Admin Manager</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Organisational Units</p>
              <p className="text-2xl font-bold text-gray-900">{stats.organisationalUnits}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.users}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Delegated Roles</p>
              <p className="text-2xl font-bold text-gray-900">{stats.delegatedRoles}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Role Verification Section */}
      <RoleVerification />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <a
              href="/organisational-units"
              className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <Building2 className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-gray-700">Manage Organisational Units</span>
            </a>
            <a
              href="/access-request"
              className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <Users className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-gray-700">Request User Access</span>
            </a>
            <a
              href="/role-delegation"
              className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <Shield className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-gray-700">Delegate Admin Roles</span>
            </a>
            {/* Role Verification Link - Commented out per cleanup request */}
            {/* 
            <a
              href="/role-verification"
              className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <CheckCircle className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-gray-700">Verify Role Permissions</span>
            </a>
            */}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
              <span>New user added to Customer 1 OU</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              <span>Admin role delegated to support team</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
              <span>Organisational unit structure updated</span>
            </div>
          </div>
        </div>
      </div>

      {/* Domain-Wide Delegation Setup Card - Commented out per cleanup request */}
      {/* 
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Domain-Wide Delegation Setup</h3>
            <p className="text-blue-800 text-sm mb-4">To access real Google Admin SDK data, authorise the service account in your Google Workspace Admin Console.</p>

            {domainTest && (
              <div className={`p-3 rounded-lg mb-4 ${domainTest.success ? 'bg-green-100 border-green-200' : 'bg-red-100 border-red-200'}`}>
                <div className="flex items-center">
                  {domainTest.success ? (<CheckCircle className="h-5 w-5 text-green-600 mr-2" />) : (<AlertCircle className="h-5 w-5 text-red-600 mr-2" />)}
                  <span className={`text-sm font-medium ${domainTest.success ? 'text-green-800' : 'text-red-800'}`}>{domainTest.message}</span>
                </div>
                {domainTest.success && domainTest.customerId && (
                  <p className="text-sm text-green-700 mt-1">Customer ID: <code className="bg-green-200 px-1 rounded">{domainTest.customerId}</code></p>
                )}
              </div>
            )}

            <div className="space-y-2 text-sm text-blue-800">
              <p><strong>Steps:</strong></p>
              <p>1. Go to <a href="https://admin.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Workspace Admin Console</a></p>
              <p>2. Security → API Controls → Domain-wide Delegation → Add new</p>
              <p>3. Client ID: <code className="bg-blue-100 px-1 rounded">{delegationInfo?.clientId || '...'}</code></p>
              <p>4. Scopes:</p>
              <div className="ml-4">
                <code className="block">https://www.googleapis.com/auth/admin.directory.user</code>
                <code className="block">https://www.googleapis.com/auth/admin.directory.orgunit</code>
                <code className="block">https://www.googleapis.com/auth/admin.directory.rolemanagement</code>
                <code className="block">https://www.googleapis.com/auth/admin.directory.customer</code>
              </div>
              {delegationInfo?.serviceAccountEmail && (
                <p className="mt-2">Service account: <code className="bg-blue-100 px-1 rounded">{delegationInfo.serviceAccountEmail}</code></p>
              )}
              {delegationInfo?.subject && (
                <p>Delegated subject (must be Super Admin): <code className="bg-blue-100 px-1 rounded">{delegationInfo.subject}</code></p>
              )}
            </div>
          </div>
          <button onClick={testDomainWideDelegation} disabled={testingDomain} className="btn-primary ml-4 whitespace-nowrap">{testingDomain ? (<div className="flex items-center"><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>Testing...</div>) : ('Test Setup')}</button>
        </div>
      </div>
      */}
    </div>
  );
};

export default Dashboard; 